import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from './Header';
import SEO from './SEO';
import { brandedProductImageUrl } from '../utils/imageUrls';
import { productPath } from '../utils/productUrls';
import { trackAppStoreCtaClick } from '../utils/analytics';
import { brandDirectory } from '../data/brandDirectory';
import './BrandPage.css';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// A few brands get a hand-written intro; everyone else falls back to the
// generic templated one built from live product stats (see `brandIntro`
// below). This is what lets `/brands/:slug` work correctly for any of the
// 700+ brands in the catalog instead of just CeraVe.
const BRAND_OVERRIDES = {
  cerave: {
    description: 'CeraVe is a dermatologist-recommended skincare brand known for its ceramide-based formulations. We\'ve analyzed every CeraVe product in our database to determine which ones are truly acne-safe.',
  },
};

function brandIntro(name, total) {
  const override = BRAND_OVERRIDES[name.toLowerCase()]?.description;
  if (override) return override;

  const countPhrase = total ? `${total} product${total === 1 ? '' : 's'}` : 'products';
  return `We've checked the ingredient list of every ${name} ${total ? 'product' : 'item'} in our database (${countPhrase}) against known pore-clogging ingredients, so you know exactly which ${name} products are safe to use if you're acne-prone.`;
}

function BrandPage() {
  const { slug } = useParams();
  const brandEntry = useMemo(() => brandDirectory.find((b) => b.slug === slug), [slug]);
  const brandName = brandEntry?.name || null;

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(Boolean(brandName));
  const [error, setError] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedSafety, setSelectedSafety] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!brandName) {
      setIsLoading(false);
      return;
    }

    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      setIsLoading(false);
      return;
    }

    let isCurrent = true;

    async function fetchProducts() {
      setIsLoading(true);
      setError('');

      try {
        const params = new URLSearchParams();
        params.set('select', [
          'id',
          'retailer',
          'brand',
          'name',
          'category',
          'product_type',
          'url',
          'is_acne_safe',
          'acne_safety_status',
          'unsafe_ingredients',
          'stored_image_url',
          'image_url',
        ].join(','));
        params.set('brand', `ilike.${brandName}`);
        params.set('order', 'name.asc');
        params.set('limit', '1000');

        const response = await fetch(`${SUPABASE_URL}/rest/v1/products?${params.toString()}`, {
          headers: {
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch products (${response.status})`);
        }

        const rows = await response.json();
        if (!isCurrent) return;

        setProducts(rows);
      } catch (err) {
        if (isCurrent) setError(err.message || 'Failed to load products.');
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    }

    fetchProducts();
    return () => {
      isCurrent = false;
    };
  }, [slug, brandName]);

  const productTypes = useMemo(() => {
    const types = new Set();
    products.forEach((product) => {
      if (product.product_type) {
        types.add(product.product_type);
      }
    });
    return Array.from(types).sort();
  }, [products]);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      if (selectedType !== 'all' && product.product_type !== selectedType) {
        return false;
      }
      
      if (selectedSafety === 'safe' && product.is_acne_safe !== true) {
        return false;
      }
      if (selectedSafety === 'not_safe' && product.acne_safety_status !== 'not_acne_safe') {
        return false;
      }
      if (selectedSafety === 'unknown' && !(product.acne_safety_status === 'unknown_ingredients' || !product.acne_safety_status)) {
        return false;
      }

      return true;
    });
  }, [products, selectedType, selectedSafety]);

  const stats = useMemo(() => {
    const safe = products.filter((p) => p.is_acne_safe === true).length;
    const notSafe = products.filter((p) => p.acne_safety_status === 'not_acne_safe').length;
    const unknown = products.length - safe - notSafe;
    
    return { total: products.length, safe, notSafe, unknown };
  }, [products]);

  function statusLabel(product) {
    if (product.acne_safety_status === 'unknown_ingredients') return 'Unknown';
    if (product.is_acne_safe === true) return 'Acne safe';
    if (product.acne_safety_status === 'not_acne_safe') return 'Not acne safe';
    return 'Unknown';
  }

  function statusClass(product) {
    if (product.is_acne_safe === true) return 'is-safe';
    if (product.acne_safety_status === 'not_acne_safe') return 'is-not-safe';
    return 'is-unknown';
  }

  const canonicalUrl = `https://www.useacnesafe.com/brands/${slug}`;

  if (!brandName) {
    return (
      <div className="App">
        <SEO
          title="Brand Not Found | Acne Safe"
          description="This brand isn't in the Acne Safe product database yet."
          url={canonicalUrl}
        />
        <Header />
        <main className="brand-page">
          <Link to="/products" className="back-link">All products</Link>
          <section className="brand-page-message">
            We don't have a dedicated page for this brand yet. Search our{' '}
            <Link to="/products">full product database</Link> to check if we've
            analyzed a specific product.
          </section>
        </main>
      </div>
    );
  }

  const title = `${brandName} Acne-Safe Products | Complete Guide`;
  const description = `Complete acne safety analysis of all ${stats.total} ${brandName} products. Find out which ${brandName} products are safe for acne-prone skin and which ones to avoid.`;

  if (isLoading) {
    return (
      <div className="App">
        <Header />
        <main className="brand-page">
          <p className="brand-page-message">Loading {brandName} products...</p>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <SEO title={`Error | ${brandName}`} description="Failed to load products" />
        <Header />
        <main className="brand-page">
          <Link to="/products" className="back-link">All products</Link>
          <section className="brand-page-message">{error}</section>
        </main>
      </div>
    );
  }

  return (
    <div className="App">
      <SEO
        title={title}
        description={description}
        keywords={`${brandName} acne safe, ${brandName} comedogenic, ${brandName} products, acne prone skin`}
        url={canonicalUrl}
        type="website"
      />
      <Header />
      <main className="brand-page">
        <Link to="/products" className="back-link">All products</Link>
        
        <div className="brand-hero">
          <h1>{brandName} Acne Safety Guide</h1>
          <p className="brand-description">{brandIntro(brandName, stats.total)}</p>
          
          <div className="brand-stats">
            <div className="stat">
              <div className="stat-number">{stats.total}</div>
              <div className="stat-label">Total Products</div>
            </div>
            <div className="stat stat-safe">
              <div className="stat-number">{stats.safe}</div>
              <div className="stat-label">Acne Safe</div>
            </div>
            <div className="stat stat-not-safe">
              <div className="stat-number">{stats.notSafe}</div>
              <div className="stat-label">Not Safe</div>
            </div>
            <div className="stat stat-unknown">
              <div className="stat-number">{stats.unknown}</div>
              <div className="stat-label">Unknown</div>
            </div>
          </div>
        </div>

        <div className="brand-filters">
          <div className="filter-group">
            <label htmlFor="product-type-filter">Product Type</label>
            <select
              id="product-type-filter"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="all">All Types ({products.length})</option>
              {productTypes.map((type) => {
                const count = products.filter((p) => p.product_type === type).length;
                return (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)} ({count})
                  </option>
                );
              })}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="safety-filter">Acne Safety</label>
            <select
              id="safety-filter"
              value={selectedSafety}
              onChange={(e) => setSelectedSafety(e.target.value)}
            >
              <option value="all">All Products ({products.length})</option>
              <option value="safe">Acne Safe ({stats.safe})</option>
              <option value="not_safe">Not Acne Safe ({stats.notSafe})</option>
              <option value="unknown">Unknown ({stats.unknown})</option>
            </select>
          </div>
        </div>

        <div className="brand-results">
          <p className="results-count">
            Showing {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
          </p>

          {filteredProducts.length === 0 ? (
            <p className="no-results">No products match your filters.</p>
          ) : (
            <div className="product-grid">
              {filteredProducts.map((product) => {
                const imageUrl = brandedProductImageUrl(product.stored_image_url || product.image_url) || '/hero-image.png';
                
                return (
                  <Link
                    key={product.id}
                    to={productPath(product)}
                    className="product-card"
                  >
                    <div className="product-card-image">
                      <img
                        src={imageUrl}
                        alt={`${product.brand || ''} ${product.name}`.trim()}
                        loading="lazy"
                        onError={(e) => {
                          if (product.stored_image_url || product.image_url) {
                            e.currentTarget.src = product.stored_image_url || product.image_url;
                          }
                        }}
                      />
                    </div>
                    <div className="product-card-content">
                      <div className={`product-status ${statusClass(product)}`}>
                        {statusLabel(product)}
                      </div>
                      {product.product_type && (
                        <div className="product-type">{product.product_type}</div>
                      )}
                      <h3 className="product-name">{product.name}</h3>
                      {product.brand && <p className="product-brand">{product.brand}</p>}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        <div className="brand-faq">
          <h2>Frequently Asked Questions</h2>
          
          <div className="faq-item">
            <h3>Is {brandName} good for acne-prone skin?</h3>
            <p>
              {brandName} has both acne-safe and non-acne-safe products. Out of {stats.total} products we analyzed, 
              {stats.safe} are acne-safe, {stats.notSafe} contain pore-clogging ingredients, and {stats.unknown} have 
              unknown ingredient safety. Always check individual products before purchasing.
            </p>
          </div>

          <div className="faq-item">
            <h3>Which {brandName} products should I avoid if I have acne?</h3>
            <p>
              Avoid {brandName} products marked as "Not acne safe" on this page. These contain comedogenic 
              ingredients that can clog pores and trigger breakouts in acne-prone individuals. Click on any product to 
              see exactly which ingredients are flagged.
            </p>
          </div>

          <div className="faq-item">
            <h3>How do you determine if a {brandName} product is acne-safe?</h3>
            <p>
              We analyze the ingredient list of each product against a database of known comedogenic (pore-clogging) 
              ingredients. Products without any flagged ingredients are marked as acne-safe. Our analysis is based on 
              research and clinical data about ingredient comedogenicity.
            </p>
          </div>

          <div className="faq-item">
            <h3>Are {brandName} moisturizers non-comedogenic?</h3>
            <p>
              Some {brandName} moisturizers are non-comedogenic, but not all. Check each moisturizer 
              individually on this page. Products labeled "Acne safe" don't contain known pore-clogging ingredients.
            </p>
          </div>
        </div>

        <div className="brand-cta">
          <h2>Check Any Product for Acne Safety</h2>
          <p>
            Want to check products beyond {brandName}? Download the Acne Safe app to scan any product's 
            ingredient list in seconds.
          </p>
          <a 
            href="https://apps.apple.com/us/app/acne-safe-ingredient-scanner/id6747936538"
            className="cta-button"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAppStoreCtaClick('brand_page')}
          >
            Download Free App
          </a>
        </div>
      </main>
    </div>
  );
}

export default BrandPage;
